import Button from "@components/_Revamp/Button";
import Input from "@components/_Revamp/Input";
import Modal from "@components/_Revamp/Modal";
import React, { useRef, useState, useReducer, useEffect } from "react";
import { useModalView } from "src/Modal/modal";
import { styled } from "@linaria/react";
import { postValidatePromoCode } from "@api/index";
import { get } from "lodash";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import {
  updatePlanOverview,
  updatePromoCode as updatePromoCodeInStore,
} from "@redux/slices/SHIELD_STARTER/step2Slice";
import { generatePlanOverView } from "../../Helpers/generatePlanOverView";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";

const PromoModal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PromoModalHeader = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  line-height: 140%;
  text-align: center;
  color: #434343;
  flex: none;
  order: 0;
  flex-grow: 1;
`;

const PromocodeModalDescription = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #8a8988;
  flex: none;
  order: 0;
  flex-grow: 1;
`;

const InputLabel = styled.div`
  margin-top: 3rem;
  align-self: flex-start;
`;

const PromoStatus = styled.div`
  align-self: flex-start;
  margin-top: 10px;
  margin-bottom: 50px;
  height: 17px;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => (props.color === "red" ? "#C50000" : "#00AC95")};
`;

interface IPromoCodeState {
  promoCode: string;
  stateMessage: string;
  isCheckButtonDisabled: boolean;
  inputBoxInvalid: boolean;
  validationSuccess: boolean;
}

enum InputState {
  UPDATE_PROMOCODE,
  DISABLE_CHECK_PROMO_BUTTON_DURING_API_CALL,
  API_CALL_INPROGRESS,
  API_CALL_RESPONDED_INVALID,
  API_CALL_RESPONDED_INELIGIBLE,
  API_CALL_RESPONDED_SUCCESS,
  API_CALL_NETWORK_ISSUE,
  DEFAULT_STATE,
}
interface IAction {
  type: InputState;
  payload: IPromoCodeState;
}

const promoCodeState: IPromoCodeState = {
  promoCode: "",
  stateMessage: "",
  inputBoxInvalid: false,
  isCheckButtonDisabled: true,
  validationSuccess: false,
};

function ShieldStarterPromoCode() {
  const history = useHistory();
  const dispatch = useAvivaDispatch();
  const whomToBuy = useAvivaSelector(
    (state) => state.shieldStarterStep1.whomToBuy
  );
  const selectedPlan = useAvivaSelector(
    (state) => state.shieldStarterStep2.policyName
  );
  const campaignCode =
    useAvivaSelector((state) => state.loading?.campaignDetail?.campaignCode) ||
    null;

  function reducer(state: IPromoCodeState, action: IAction) {
    switch (action.type) {
      case InputState.UPDATE_PROMOCODE:
        return {
          stateMessage: "",
          promoCode: action.payload.promoCode,
          inputBoxInvalid: false,
          isCheckButtonDisabled: false,
          validationSuccess: false,
        };
      case InputState.API_CALL_INPROGRESS:
        return {
          ...state,
          stateMessage: "",
          inputBoxInvalid: false,
          isCheckButtonDisabled: true,
          validationSuccess: false,
        };
      case InputState.API_CALL_RESPONDED_INVALID:
        return {
          ...state,
          stateMessage: "Promo code is invalid",
          inputBoxInvalid: true,
          isCheckButtonDisabled: true,
          validationSuccess: false,
        };
      case InputState.API_CALL_NETWORK_ISSUE:
        return {
          ...state,
          stateMessage: "Please check your internet connection",
          inputBoxInvalid: true,
          isCheckButtonDisabled: true,
          validationSuccess: false,
        };
      case InputState.API_CALL_RESPONDED_INELIGIBLE:
        return {
          ...state,
          stateMessage:
            "Looks like you're not eligible for this code, try another one?",
          inputBoxInvalid: true,
          isCheckButtonDisabled: true,
          validationSuccess: false,
        };
      case InputState.API_CALL_RESPONDED_SUCCESS:
        return {
          ...state,
          stateMessage: "Promo has been successfully applied.",
          inputBoxInvalid: false,
          isCheckButtonDisabled: false,
          validationSuccess: true,
        };
      case InputState.DISABLE_CHECK_PROMO_BUTTON_DURING_API_CALL:
        return {
          ...state,
          isCheckButtonDisabled: true,
        };
      case InputState.DEFAULT_STATE:
      default:
        return promoCodeState;
    }
  }
  const [inputState, dispatchInputState] = useReducer(reducer, promoCodeState);
  const [showModal, setShowModal] = useState(true);
  const [, setModalState] = useModalView();

  const updatePromoCode: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value !== " ") {
      dispatchInputState({
        type: InputState.UPDATE_PROMOCODE,
        payload: { ...inputState, promoCode: e.target.value.toUpperCase() },
      });
    }
  };

  const promoCodeInputBoxRef: any = useRef();

  const onclose = () => {
    setShowModal(false);
    setModalState({ isModalShow: false, ModalViewComponent: "" });
  };
  async function validatePromoCode() {
    try {
      dispatchInputState({
        type: InputState.DISABLE_CHECK_PROMO_BUTTON_DURING_API_CALL,
        payload: { ...inputState },
      });
      const { data, status } = await postValidatePromoCode(
        inputState.promoCode,
        selectedPlan,
        whomToBuy,
        campaignCode
      );
      if (status === 200 && !data.error) {
        //
        //Commenting for the future reference, once SQS re-implements the discountEligible & firstYearDiscountEligible flags
        //
        // const discountEligible = get(
        //   data,
        //   "data.coverages[0].attributes.discountEligible",
        //   false
        // );
        // const firstYearDiscountEligible = get(
        //   data,
        //   "data.coverages[0].attributes.firstYrDiscountEligible",
        //   false
        // );
        const checkIfDiscountObject = get(
          data,
          "data.coverages[0].attributes.discountedPremium",
          null
        );
        const discountedPremiumObject =
          checkIfDiscountObject &&
          Object.entries(checkIfDiscountObject)?.filter(
            (entry) => entry[1] !== null
          );

        const isDiscountApplicable =
          checkIfDiscountObject && discountedPremiumObject?.length > 0;

        if (isDiscountApplicable) {
          dispatchInputState({
            type: InputState.API_CALL_RESPONDED_SUCCESS,
            payload: { ...inputState },
          });

          let planOverview = generatePlanOverView(data?.data, () => {});
          console.log("from modal promo", data);

          dispatch(updatePlanOverview(planOverview));
          dispatch(updatePromoCodeInStore(inputState.promoCode));
        }
        // else if(!firstYearDiscountEligible && discountEligible){
        //   dispatchInputState({
        //     type: InputState.API_CALL_RESPONDED_INELIGIBLE,
        //     payload: { ...inputState },
        //   });
        // }
        else {
          dispatchInputState({
            type: InputState.API_CALL_RESPONDED_INVALID,
            payload: { ...inputState },
          });
        }
      } else {
        dispatchInputState({
          type: InputState.API_CALL_NETWORK_ISSUE,
          payload: { ...inputState },
        });
        history.push(ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR);
      }
    } catch {
      dispatchInputState({
        type: InputState.API_CALL_NETWORK_ISSUE,
        payload: { ...inputState },
      });
      setModalState({ isModalShow: false, ModalViewComponent: "" });
      history.push(ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR);
    }
  }

  useEffect(() => {
    promoCodeInputBoxRef?.current?.focus();
  }, []);

  return (
    <Modal show={showModal} onClose={onclose}>
      <PromoModal>
        <PromoModalHeader>Key in your Promo Code</PromoModalHeader>
        <PromocodeModalDescription>
          Always good to have a better bargain
        </PromocodeModalDescription>
        <InputLabel>Promo code</InputLabel>
        <Input
          margin={0}
          maxLength={4}
          ref={promoCodeInputBoxRef}
          fontWeight={"normal"}
          value={inputState.promoCode}
          onChange={updatePromoCode}
          invalid={inputState.inputBoxInvalid}
        />
        <PromoStatus color={inputState.inputBoxInvalid ? "red" : "green"}>
          {inputState.stateMessage}
        </PromoStatus>
        <Button
          width="40%"
          label={inputState.validationSuccess ? "Close" : "Check promo"}
          disabled={
            inputState.isCheckButtonDisabled ||
            inputState.promoCode.trim() === ""
          }
          onClick={() => {
            inputState.validationSuccess ? onclose() : validatePromoCode();
          }}
        />
      </PromoModal>
    </Modal>
  );
}

export default ShieldStarterPromoCode;
